import * as React from "react";
import { useTheme } from "react-jss";
import useStyles from "./CarrouselControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import { useValidation, useDiscreteVariadicValidator } from "../../hooks/useValidation";
import {
    getSettingBoolean,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceListControl } from "./ChoiceListControl";
import useDimensionContext from "../../hooks/useDimensionContext";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import ClampLines from "react-clamp-lines";
import { QuestionType } from "../../models/enums/QuestionType";

interface StatementProps {
    id: string;
    key: string;
    text: string;
    color: string;
    imageUrl: string;
    isMobileView: boolean;
}

const MOBILE_DIMENSIONS = 293;
const DESKTOP_DIMENSIONS = 460;
const RESIZE_CAROUSEL_DEFAULT_TEXT_LINES = 15;

const Statement = (props: StatementProps) => {
    const { id, color, text, imageUrl, isMobileView } = props;
    const theme: any = useTheme();
    const { ContentResponsivenessOption: contentResponsivenessOption } = theme;
    const justifyContent = imageUrl ? "space-between" : "center";
    const classes = useStyles({
        theme,
        color,
        justifyContent,
        isMobileView,
        contentResponsivenessOption,
    });
    const containerRef = React.useRef(null);
    const imageRef = React.useRef(null);
    const textContainerRef = React.useRef(null);
    const [linesToShow, setLinesToShow] = React.useState(RESIZE_CAROUSEL_DEFAULT_TEXT_LINES);
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);

    React.useEffect(() => {
        const container = containerRef.current;
        const textContainer = textContainerRef.current;
        const image = imageRef.current;
        if (image) {
            if (image.complete) {
                container.style.height = "auto";
                textContainer.style.height = "auto";
                image.style.width = "";
                image.style.height = "";
                responsiveRender();
            } else {
                image.addEventListener("load", responsiveRender);
            }
        } else {
            resizeFont();
        }

        return () => {
            image && image.removeEventListener("load", responsiveRender);
        };

        function responsiveRender() {
            contentResponsivenessOption == "resize-carousel"
                ? resizeCarousel(image, container)
                : resizeImage(image, container);
            resizeFont();
            calculateLinesToShow(textContainer, image);
        }

        function resizeFont() {
            const fontSize = getFontSize(container);
            container.style.fontSize = `${fontSize}px`;
        }
    }, [imageUrl, isMobileView, contentResponsivenessOption]);

    const calculateLinesToShow = (textContainer, image) => {
        if (contentResponsivenessOption === "resize-carousel") {
            setForceUpdateKey((prevKey) => prevKey + 1);
            return;
        }

        textContainer.style.height = `${
            (isMobileView ? MOBILE_DIMENSIONS : DESKTOP_DIMENSIONS) - image.height
        }px`;

        const lineHeight = parseFloat(getComputedStyle(textContainer).lineHeight);
        const containerHeight =
            textContainer.clientHeight -
            parseFloat(getComputedStyle(textContainer).paddingTop) -
            parseFloat(getComputedStyle(textContainer).paddingBottom) -
            lineHeight;

        const calculatedLines = Math.floor(containerHeight / lineHeight);
        setLinesToShow(calculatedLines <= 0 ? 1 : calculatedLines);
        setForceUpdateKey((prevKey) => prevKey + 1);
        containerRef.current.style.height = `${
            isMobileView ? MOBILE_DIMENSIONS : DESKTOP_DIMENSIONS
        }px`;
    };

    const resizeImage = (image, container) => {
        if (!image) return;
        let containerWidth = containerRef.current.offsetWidth;
        let containerHeight = isMobileView ? 235 : 400;
        const imageWidth = image.naturalWidth;
        const imageHeight = image.naturalHeight;

        const widthScaleFactor = containerWidth / imageWidth;
        const heightScaleFactor = containerHeight / imageHeight;
        const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

        const scaledWidth = Math.floor(imageWidth * scaleFactor);
        const scaledHeight = Math.floor(imageHeight * scaleFactor);

        image.style.width = `${scaledWidth}px`;
        image.style.height = `${scaledHeight}px`;

        if (
            contentResponsivenessOption == "resize-content" ||
            contentResponsivenessOption == "crop-content"
        ) {
            container.style.width = `${isMobileView ? MOBILE_DIMENSIONS : DESKTOP_DIMENSIONS}px`;
        }
    };

    const resizeCarousel = (image, container) => {
        if (!image) return;
        const MAX_STATEMENT_RESIZE_WIDTH = 694;
        const MIN_STATEMENT_RESIZE_WIDTH = 460;

        setLinesToShow(RESIZE_CAROUSEL_DEFAULT_TEXT_LINES);
        const imageWidth = image.naturalWidth;

        if (imageWidth > MAX_STATEMENT_RESIZE_WIDTH) {
            const scaledWidth = MAX_STATEMENT_RESIZE_WIDTH;
            image.style.width = `${scaledWidth}px`;
            container.style.width = !isMobileView ? `${scaledWidth}px` : "";
        }

        if (imageWidth >= MIN_STATEMENT_RESIZE_WIDTH && imageWidth <= MAX_STATEMENT_RESIZE_WIDTH) {
            image.style.width = `${imageWidth}px`;
            container.style.width = !isMobileView ? `${imageWidth}px` : "";
        }

        if (imageWidth < MIN_STATEMENT_RESIZE_WIDTH) {
            image.style.width = `${imageWidth}px`;
            container.style.width = !isMobileView ? `${MIN_STATEMENT_RESIZE_WIDTH}px` : "";
        }
    };

    const getFontSize = (container) => {
        const DEFAULT_FONT_SIZE = 16;
        const MINIMUM_FONT_SIZE = 12;
        const MAXIMUM_FONT_SIZE = 30;
        const containerWidth = container.offsetWidth;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        context.font = `${DEFAULT_FONT_SIZE}px`;
        const textWidth = context.measureText(text).width;

        let fontSize = Math.floor((containerWidth / textWidth) * DEFAULT_FONT_SIZE);
        fontSize = Math.max(fontSize, MINIMUM_FONT_SIZE);
        fontSize = Math.min(fontSize, MAXIMUM_FONT_SIZE);

        if (textWidth < containerWidth && fontSize < DEFAULT_FONT_SIZE) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        if (
            contentResponsivenessOption == "resize-carousel" ||
            contentResponsivenessOption == "crop-content"
        ) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        return fontSize;
    };

    return (
        <div className={classes.statement} id={id} ref={containerRef}>
            {imageUrl && (
                <span className="image-wrapper">
                    <img
                        className={`fade-in-element ${
                            contentResponsivenessOption === "crop-content" ? "crop-image" : ""
                        }`}
                        src={imageUrl}
                        ref={imageRef}
                        alt={imageUrl}
                    />
                </span>
            )}
            <div className="statementTextContent fade-in-element" ref={textContainerRef}>
                <ClampLines
                    id="statementTextContent"
                    text={text}
                    lines={linesToShow}
                    ellipsis="..."
                    buttons={false}
                    key={forceUpdateKey}
                />
            </div>
        </div>
    );
};

const WrapperWithNavigationButtons = ({
    children,
    classes,
    isPrevBtnActive,
    isNextBtnActive,
    handleNavigationButtonClick,
    isMobileView,
}) => {
    return (
        <div className={classes.navigationButtonsPanel}>
            {isMobileView && (
                <div className="fixedWidth">
                    {isPrevBtnActive && (
                        <FontAwesomeIcon
                            icon={["fas", "arrow-left"]}
                            fixedWidth
                            className={classes.navigationButton}
                            onClick={() => handleNavigationButtonClick("prev")}
                        />
                    )}
                </div>
            )}
            {children}
            {isMobileView && (
                <div className="fixedWidth">
                    {isNextBtnActive && (
                        <FontAwesomeIcon
                            icon={["fas", "arrow-right"]}
                            fixedWidth
                            className={classes.navigationButton}
                            onClick={() => handleNavigationButtonClick("next")}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export function CarrouselControl(props: ControlProps) {
    const { question, sequencedVariables, validateSignal } = props;

    let hideVariableBackArrow = false;
    let canLoopVariable = false;
    let advanceAutomatically = false;
    let choiceListAnswerType = QuestionType[QuestionType.CheckBox];

    if (hasSetting(question, "HideVariableBackArrow")) {
        hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
    }

    if (hasSetting(question, "LoopVariable")) {
        canLoopVariable = getSettingBoolean(question, "LoopVariable");
    }

    if (hasSetting(question, "AutoAdvanceVariable")) {
        advanceAutomatically = getSettingBoolean(question, "AutoAdvanceVariable");
    }

    if (hasSetting(question, "CarrouselChoiceListAnswerType")) {
        choiceListAnswerType = getSettingString(question, "CarrouselChoiceListAnswerType");
    }

    const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
    const [language] = useLanguage();
    const theme = useTheme();
    const counter = React.useRef(1);
    const [isPrevBtnActive, setIsPrevBtnActive] = React.useState(backButtonAlwaysVisible);
    const [carrouselActiveVariableIndex, setCarrouselActiveVariableIndex] = React.useState(0);
    const { innerWidth: pageWrapperWidth } = useDimensionContext();
    const [isMobileView, setIsMobileView] = React.useState(false);
    const justifyContent = isMobileView ? "space-between" : "center";
    const classes = useStyles({ theme, isMobileView, justifyContent });
    const activeVariable = sequencedVariables[carrouselActiveVariableIndex];

    React.useEffect(() => {
        setIsMobileView(pageWrapperWidth <= 768);
    }, [pageWrapperWidth]);

    const scrollHandler = (direction, vars) => {
        if (direction === "prev") {
            if (canLoopVariable && counter.current === 1) {
                counter.current = vars.length;
            } else {
                counter.current--;
            }

            setIsPrevBtnActive(backButtonAlwaysVisible || counter.current > 1);
        } else if (direction === "next") {
            if (canLoopVariable && counter.current === vars.length) {
                counter.current = 1;
            } else {
                counter.current++;
            }

            setIsPrevBtnActive(!hideVariableBackArrow);
        }

        setCarrouselActiveVariableIndex(counter.current - 1);
    };

    const handleNavigationButtonClick = React.useCallback(
        (direction) => {
            scrollHandler(direction, sequencedVariables);
        },
        [sequencedVariables],
    );

    const onAfterCarouselChoiceSelection = React.useCallback(() => {
        if (
            advanceAutomatically &&
            choiceListAnswerType === QuestionType[QuestionType.RadioButton]
        ) {
            if (sequencedVariables.length - 1 > carrouselActiveVariableIndex) {
                handleNavigationButtonClick("next");
            }
        }
    }, [
        sequencedVariables,
        carrouselActiveVariableIndex,
        advanceAutomatically,
        choiceListAnswerType,
        handleNavigationButtonClick,
    ]);

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const commonProps = {
        isNextBtnActive: canLoopVariable || counter.current !== sequencedVariables.length,
        isPrevBtnActive,
        handleNavigationButtonClick,
        classes,
    };

    return (
        <div className={classes.container} style={{ width: pageWrapperWidth }}>
            <WrapperWithNavigationButtons {...commonProps} isMobileView={!isMobileView}>
                <div className={classes.statementsContainer}>
                    <Statement
                        id={activeVariable.elementId}
                        key={activeVariable.name}
                        color={getSettingString(activeVariable, "Color", "#F2F2F2")}
                        text={translate(activeVariable, "text", language)}
                        imageUrl={translate(activeVariable, "imageUrl", language)}
                        isMobileView={isMobileView}
                    />
                </div>
            </WrapperWithNavigationButtons>
            <WrapperWithNavigationButtons {...commonProps} isMobileView={isMobileView}>
                <div className="fixedWidth">{`${counter.current}/${sequencedVariables.length}`}</div>
            </WrapperWithNavigationButtons>
            <ControlContainer
                controlType={ChoiceListControl}
                carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                onAfterSelection={onAfterCarouselChoiceSelection}
                isSingleOnPage={props.isSingleOnPage}
                onNavigation={props.onNavigation}
                {...getRendererProps(props)}
            />
        </div>
    );
}
